import { Requisition, SubSamples } from '../../types/types'
import { SubSampleItem } from '../sub-sample-item/SubSampleItem'
import { Mtds } from 'prove-common'
import { useState } from 'react'
import { getResultLines, SampleStatus } from '../../utils/sampleUtils'

const ITEMS_PER_PAGE = 10

type SubSamplesSectionProps = {
  sampleId: string
  subSamples: SubSamples
  requisition: Requisition
  sampleStatus: SampleStatus
}

export const SubSamplesSection = ({
  sampleId,
  subSamples,
  requisition,
  sampleStatus
}: SubSamplesSectionProps) => {
  const results = requisition?.results
  const [page, setPage] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE)
  const totalPages = Math.ceil(subSamples.length / itemsPerPage)
  const paginatedSamples =
    itemsPerPage >= subSamples.length
      ? [...subSamples].sort((a, b) => {
          const resultA =
            getResultLines(results?.subSamples?.[a.subSampleId]).length ??
            -Infinity
          const resultB =
            getResultLines(results?.subSamples?.[b.subSampleId]).length ??
            -Infinity
          return resultB - resultA || a.subSampleId - b.subSampleId
        })
      : [...subSamples]
          .sort((a, b) => {
            const resultA =
              getResultLines(results?.subSamples?.[a.subSampleId]).length ??
              -Infinity
            const resultB =
              getResultLines(results?.subSamples?.[b.subSampleId]).length ??
              -Infinity
            return resultB - resultA || a.subSampleId - b.subSampleId
          })
          .slice(page * itemsPerPage, (page + 1) * itemsPerPage)
  return (
    <Mtds.Grid data-gap='4' data-align-content='start'>
      <Mtds.Heading as='h2' data-size='sm'>
        Delprøver <Mtds.Badge data-badge={subSamples.length} />
      </Mtds.Heading>
      {paginatedSamples.length ? (
        paginatedSamples
          .toSorted((a, b) => a.subSampleId - b.subSampleId)
          .map((subSample, index) => (
            <SubSampleItem
              key={`${subSample.subSampleId}_${index}`}
              sampleId={sampleId}
              requisitionId={requisition.id}
              subSample={subSample}
              analysisType={requisition.analysisType}
              result={results?.subSamples?.[subSample.subSampleId]}
              complete={results?.complete}
              payloadSchema={requisition.analysisType.payloadSchema}
              sampleStatus={sampleStatus}
            />
          ))
      ) : (
        <Mtds.Card>Denne prøven består ikke av noen delprøver</Mtds.Card>
      )}
      {subSamples.length > ITEMS_PER_PAGE && (
        <Mtds.Flex data-align='center' data-justify='space-between'>
          <p>
            Viser{' '}
            {Math.min((page + 1) * itemsPerPage, subSamples.length) -
              page * itemsPerPage}{' '}
            av {subSamples.length}
          </p>

          <Mtds.Pagination
            data-size='sm'
            current={page + 1}
            total={totalPages}
            props={({ page: p }) => ({
              onClick: () => {
                if (p === totalPages) {
                  window.scrollTo({ top: 0 })
                }
                setPage(p - 1)
              }
            })}
          />
          <Mtds.Field
            as='select'
            value={itemsPerPage || '20'}
            onChange={({ target }) => {
              window.scrollTo({ top: 0 })
              setPage(0)
              setItemsPerPage(Number(target.value))
            }}
            options={[
              { value: '5', label: 'Vis 5 pr. side' },
              { value: '10', label: 'Vis 10 pr. side' },
              { value: '20', label: 'Vis 20 pr. side' },
              { value: '40', label: 'Vis 40 pr. side' },
              { value: '60', label: 'Vis 60 pr. side' }
            ]}
          />
        </Mtds.Flex>
      )}
    </Mtds.Grid>
  )
}
